/**
 * Data Export Service
 * Handles exporting aligned consumption and price data
 */

import apiClient from './client';

export interface ExportOptions {
  format: 'csv' | 'excel';
  projectId?: string;
}

export interface ExportSummary {
  consumptionRecords: number;
  priceRecords: number;
  alignedRecords: number;
  canExport: boolean;
  project: {
    id: string;
    name: string;
  };
}

/**
 * Export aligned data as CSV or Excel
 */
export const exportAlignedData = async (options: ExportOptions): Promise<void> => {
  try {
    const response = await fetch('/api/data/export/aligned', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        format: options.format,
        project_id: options.projectId
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.message || 'Export failed');
    }

    // Get the filename from the Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = `aligned_data.${options.format}`;
    
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename=(.+)/);
      if (filenameMatch) {
        filename = filenameMatch[1].replace(/"/g, '');
      }
    }

    // Convert response to blob
    const blob = await response.blob();
    
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    
    // Trigger download
    document.body.appendChild(link);
    link.click();
    
    // Cleanup
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 100);
    
  } catch (error) {
    console.error('Export failed:', error);
    throw error;
  }
};

/**
 * Get summary of available data for export
 */
export const getExportSummary = async (): Promise<ExportSummary> => {
  try {
    const response = await apiClient.get('/api/data/export/summary');
    
    if (response.data.success) {
      return response.data.data;
    } else {
      throw new Error(response.data.message || 'Failed to get export summary');
    }
  } catch (error) {
    console.error('Failed to get export summary:', error);
    throw error;
  }
};

/**
 * Download file utility
 */
export const downloadFile = (blob: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  
  document.body.appendChild(link);
  link.click();
  
  // Cleanup
  setTimeout(() => {
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }, 100);
};